import { useState } from 'react';
import { useLocation } from 'wouter';
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { Button } from '../components/ui/Button.js';
import { useAuth } from '../context/AuthContext.js';
import { ApiError, auth as authApi, type AuthResult } from '../lib/api.js';

function authError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return 'Too many attempts. Please wait a few minutes and try again.';
    if (error.message === 'invalid-credentials') return 'The email or password is incorrect.';
    return error.message;
  }
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return 'Passkey sign-in was cancelled or timed out.';
  }
  return fallback;
}

export default function LoginPage() {
  const { setAuth } = useAuth();
  const [, navigate] = useLocation();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('oauth') === 'failed'
      ? 'Google sign-in could not be completed. Please try again.'
      : null,
  );
  const [busy, setBusy] = useState(false);

  const finish = (result: AuthResult) => {
    setAuth(result.accessToken, result.user);
    navigate('/');
  };

  const run = async (action: () => Promise<void>, fallback: string) => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      setError(authError(caught, fallback));
    } finally {
      setBusy(false);
    }
  };

  const usePasskey = () =>
    run(async () => {
      const options =
        (await authApi.getPasskeyLoginOptions()) as unknown as PublicKeyCredentialRequestOptionsJSON;
      const credential = await startAuthentication({ optionsJSON: options });
      finish(await authApi.verifyPasskeyLogin(credential));
    }, 'Could not sign in with your passkey.');

  const sendMagicLink = () =>
    run(async () => {
      await authApi.requestMagicLink(email);
      setEmailSent(true);
    }, 'Could not send the sign-in link.');

  return (
    <main className="min-h-screen grid bg-bg lg:grid-cols-[minmax(18rem,0.8fr)_minmax(24rem,1.2fr)]">
      <section
        className="hidden flex-col justify-between bg-text p-12 text-bg lg:flex"
        aria-hidden="true"
      >
        <div className="text-sm font-semibold uppercase tracking-widest">AetherCasa</div>
        <div>
          <p className="max-w-md text-4xl font-semibold leading-tight">Your home stays yours.</p>
          <p className="mt-4 max-w-sm opacity-70">
            Secure access without surrendering control of your home.
          </p>
        </div>
        <div className="text-sm opacity-60">Private by design</div>
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-9">
            <div className="mb-10 text-sm font-semibold uppercase tracking-widest text-accent lg:hidden">
              AetherCasa
            </div>
            <p className="text-sm font-medium text-accent">Welcome back</p>
            <h1 className="mt-2 text-3xl font-semibold text-text">Sign in to your home</h1>
          </div>

          {error ? (
            <div
              role="alert"
              className="mb-5 border-l-4 border-error bg-surface-elevated px-4 py-3 text-sm text-error"
            >
              {error}
            </div>
          ) : null}
          {emailSent ? (
            <div
              role="status"
              className="mb-5 border-l-4 border-success bg-surface-elevated px-4 py-3 text-sm text-text"
            >
              Check your inbox. The link expires soon and can only be used once.
            </div>
          ) : null}

          <div className="space-y-4">
            <label className="block text-sm font-medium text-text">
              Email address
              <input
                className="mt-2 w-full border border-border bg-surface px-3 py-3 text-text"
                type="email"
                autoComplete="email"
                required
                disabled={busy}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailSent(false);
                }}
              />
            </label>

            <Button className="w-full" disabled={busy} onClick={() => void usePasskey()}>
              {busy ? 'Please wait…' : 'Continue with passkey'}
            </Button>
            <a
              className="inline-flex w-full items-center justify-center rounded-lg border border-border bg-surface-elevated px-4 py-2 font-medium text-text transition-colors hover:bg-surface"
              href={authApi.googleSignInUrl()}
            >
              Continue with Google
            </a>
            <Button
              className="w-full"
              variant="secondary"
              disabled={busy || !email || emailSent}
              onClick={() => void sendMagicLink()}
            >
              Email me a secure link
            </Button>
          </div>

          <p className="mt-5 text-sm text-text-muted">
            New accounts are created after verifying the emailed secure link.
          </p>
        </div>
      </section>
    </main>
  );
}
